import {
  COASTING_DETECTION_THRESHOLD,
  GAP_FILL_MAX_LENGTH_POINTS,
  GAP_FILL_MIN_SURROUNDING_INTENSITY,
  ANOMALY_DETECTION_WINDOW_POINTS,
  ISOLATED_COASTING_THRESHOLD,
  SIGNIFICANT_COASTING_THRESHOLD,
  ANOMALY_FILL_REDUCTION_FACTOR,
  POST_SMOOTHING_SUPPRESSION_THRESHOLD,
  SPIKE_REMOVAL_MIN_PEAK_INTENSITY,
  SPIKE_REMOVAL_MIN_DURATION_MS,
  SPIKE_REMOVAL_MAX_LENGTH_POINTS,
} from './constants'
import { type CoastingPeriod } from './types'

/**
 * Remove isolated anomalous coasting points.
 *
 * Isolated high coasting in productive riding → GPS glitch, remove
 * Isolated low point in coasting section → Fill gap with interpolation
 */
export const removeAnomalies = (
  coastingPeriods: CoastingPeriod[]
): CoastingPeriod[] => {
  if (coastingPeriods.length < 5) return coastingPeriods

  const filtered: CoastingPeriod[] = []

  for (let i = 0; i < coastingPeriods.length; i++) {
    const current = coastingPeriods[i]

    // Get surrounding context (±3 points)
    const contextStart = Math.max(0, i - ANOMALY_DETECTION_WINDOW_POINTS)
    const contextEnd = Math.min(
      coastingPeriods.length,
      i + ANOMALY_DETECTION_WINDOW_POINTS + 1
    )
    const contextPeriods = coastingPeriods.slice(contextStart, contextEnd)

    const significantCoastingCount = contextPeriods.filter(
      (p) => p.coastingIntensity > SIGNIFICANT_COASTING_THRESHOLD
    ).length

    // Case 1: Isolated high coasting point surrounded by productive riding
    // -> Set to zero (likely a GPS glitch)
    if (
      current.coastingIntensity > ISOLATED_COASTING_THRESHOLD &&
      significantCoastingCount <= 2
    ) {
      filtered.push({
        ...current,
        coastingIntensity: 0,
      })
    }
    // Case 2: Isolated low point surrounded by coasting
    // -> Fill the gap with interpolated value
    else if (
      current.coastingIntensity < POST_SMOOTHING_SUPPRESSION_THRESHOLD &&
      significantCoastingCount >= 4
    ) {
      const surroundingIntensities = contextPeriods
        .filter((p) => p.coastingIntensity > SIGNIFICANT_COASTING_THRESHOLD)
        .map((p) => p.coastingIntensity)

      if (surroundingIntensities.length > 0) {
        const avgIntensity =
          surroundingIntensities.reduce((a, b) => a + b, 0) /
          surroundingIntensities.length
        filtered.push({
          ...current,
          coastingIntensity: avgIntensity * ANOMALY_FILL_REDUCTION_FACTOR,
        })
      } else {
        filtered.push(current)
      }
    } else {
      filtered.push(current)
    }
  }

  return filtered
}

/**
 * Fill small gaps (1-3 points) between coasting sections via linear interpolation.
 *
 * Why? Reduces fragmentation, creates more cohesive coasting regions.
 */
export const fillCoastingGaps = (
  periods: CoastingPeriod[]
): CoastingPeriod[] => {
  if (periods.length < 3) return periods

  const result = [...periods]

  for (let i = 0; i < result.length; i++) {
    // Skip if current point is already coasting
    if (result[i].coastingIntensity >= COASTING_DETECTION_THRESHOLD) continue

    // Find the extent of the gap (consecutive non-coasting points)
    let gapStart = i
    let gapEnd = i

    while (
      gapStart > 0 &&
      result[gapStart - 1].coastingIntensity < COASTING_DETECTION_THRESHOLD
    ) {
      gapStart--
    }

    while (
      gapEnd < result.length - 1 &&
      result[gapEnd + 1].coastingIntensity < COASTING_DETECTION_THRESHOLD
    ) {
      gapEnd++
    }

    const gapLength = gapEnd - gapStart + 1

    // Only fill small gaps (1-3 points)
    if (gapLength <= GAP_FILL_MAX_LENGTH_POINTS) {
      const hasPrevCoasting =
        gapStart > 0 &&
        result[gapStart - 1].coastingIntensity >=
          GAP_FILL_MIN_SURROUNDING_INTENSITY
      const hasNextCoasting =
        gapEnd < result.length - 1 &&
        result[gapEnd + 1].coastingIntensity >=
          GAP_FILL_MIN_SURROUNDING_INTENSITY

      // Only fill if there's significant coasting on both sides
      if (hasPrevCoasting && hasNextCoasting) {
        const prevIntensity = result[gapStart - 1].coastingIntensity
        const nextIntensity = result[gapEnd + 1].coastingIntensity

        // Linear interpolation
        for (let j = gapStart; j <= gapEnd; j++) {
          const t = (j - gapStart + 1) / (gapLength + 1)
          const interpolatedIntensity =
            prevIntensity + (nextIntensity - prevIntensity) * t

          result[j] = {
            ...result[j],
            coastingIntensity: interpolatedIntensity,
          }
        }
      }
    }

    // Skip past the gap we just processed
    i = gapEnd
  }

  return result
}

/**
 * Remove isolated spikes before final smoothing.
 *
 * Why before smoothing? Prevents spreading noise via Gaussian kernel.
 * Removes spikes that are too weak (<35%) or too brief (<0.6s).
 */
export const removeIsolatedSpikes = (
  periods: CoastingPeriod[]
): CoastingPeriod[] => {
  if (periods.length < 5) return periods

  const result = [...periods]

  for (let i = 0; i < result.length; i++) {
    // Skip non-coasting points
    if (result[i].coastingIntensity < COASTING_DETECTION_THRESHOLD) continue

    // Find the extent of the coasting section
    let sectionStart = i
    let sectionEnd = i

    while (
      sectionStart > 0 &&
      result[sectionStart - 1].coastingIntensity >= COASTING_DETECTION_THRESHOLD
    ) {
      sectionStart--
    }

    while (
      sectionEnd < result.length - 1 &&
      result[sectionEnd + 1].coastingIntensity >= COASTING_DETECTION_THRESHOLD
    ) {
      sectionEnd++
    }

    const sectionLength = sectionEnd - sectionStart + 1

    // Only check short sections (potential spikes)
    if (sectionLength <= SPIKE_REMOVAL_MAX_LENGTH_POINTS) {
      // Calculate duration and peak intensity
      const startTime =
        result[sectionStart].timeBasedLocation.gpsPoint.elapsedMs
      const endTime = result[sectionEnd].timeBasedLocation.gpsPoint.elapsedMs
      const durationMs = endTime - startTime

      let peakIntensity = 0
      for (let j = sectionStart; j <= sectionEnd; j++) {
        peakIntensity = Math.max(peakIntensity, result[j].coastingIntensity)
      }

      // Remove if spike is too weak or too brief
      const shouldRemove =
        peakIntensity < SPIKE_REMOVAL_MIN_PEAK_INTENSITY ||
        durationMs < SPIKE_REMOVAL_MIN_DURATION_MS

      if (shouldRemove) {
        for (let j = sectionStart; j <= sectionEnd; j++) {
          result[j] = {
            ...result[j],
            coastingIntensity: 0,
          }
        }
      }
    }

    // Skip past the section we just processed
    i = sectionEnd
  }

  return result
}
